<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10060]"
    @click.self="closeModal"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden" @click.stop>
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {{ props.lead ? t('kanban.schedule.scheduleLead') : (props.cliente ? t('workspaceClienteModal.agendamento.scheduleCliente') : t('kanban.schedule.scheduleTask')) }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('kanban.schedule.subtitle') }}</p>
          </div>
        </div>
        <button @click="closeModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="flex h-[700px]">
        <!-- Calendar Column -->
        <div class="w-1/2 border-r border-gray-200 dark:border-gray-700 p-6">
          <div class="mb-4">
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{{ t('kanban.schedule.selectDate') }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ props.lead ? t('kanban.schedule.selectDateHintLead') : t('kanban.schedule.selectDateHintTask') }}
            </p>
          </div>

          <!-- Calendar Navigation -->
          <div class="flex items-center justify-between mb-4">
            <button @click="previousMonth" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h5 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ currentMonth.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' }) }}
            </h5>
            <button @click="nextMonth" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>

          <!-- Calendar Grid -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div v-for="day in weekdayLabels" 
                 :key="day" 
                 class="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2">
              {{ day }}
            </div>
          </div>

          <div class="schedule-calendar-dates-grid grid grid-cols-7 gap-1">
            <button v-for="day in calendarDays" 
                    :key="day.date" 
                    @click="selectDate(day.date)"
                    :disabled="!day.isCurrentMonth || day.isPast"
                    class="schedule-calendar-day-btn relative p-3 text-sm rounded-lg transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    :class="{
                      'bg-blue-500 text-white hover:bg-blue-600': selectedDate && isSameDay(selectedDate, day.date),
                      'bg-gray-100 dark:bg-gray-700 text-gray-500': !day.isCurrentMonth,
                      'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': day.scheduleCount > 0 && day.isCurrentMonth && !day.isPast,
                      'text-gray-900 dark:text-white': day.isCurrentMonth && day.scheduleCount === 0 && !day.isPast
                    }">
              {{ day.day }}
              <div v-if="day.scheduleCount > 0 && day.isCurrentMonth && !day.isPast" 
                   class="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                {{ day.scheduleCount }}
              </div>
            </button>
          </div>
        </div>

        <!-- Time Slots and Motivos Column -->
        <div class="w-1/2 p-4">
          <div class="mb-3">
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">{{ t('kanban.schedule.selectTimeAndReason') }}</h4>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ selectedDate ? t('kanban.schedule.timesForDate', { date: formatSelectedDate(selectedDate) }) : t('kanban.schedule.selectDateFirst') }}
            </p>
          </div>

          <div v-if="selectedDate" class="flex gap-3 h-[620px]">
            <!-- Debug: Mostrar informações da data selecionada -->
            <div class="text-xs text-gray-500 mb-2 absolute top-2 right-2">
              Debug: Data selecionada = {{ selectedDate ? 'SIM' : 'NÃO' }}, TimeSlots = {{ timeSlots.length }}
            </div>
            
            <!-- Time Slots - Coluna Esquerda -->
            <div class="w-1/2">
              <div class="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 h-full">
                <h5 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                  <svg class="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {{ t('kanban.schedule.times') }}
                </h5>
                <div class="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                  <div v-for="timeSlot in timeSlots" 
                       :key="timeSlot.time"
                       @click="selectTimeSlot(timeSlot)"
                       class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer transition-all duration-200 hover:bg-white dark:hover:bg-gray-600 hover:shadow-md"
                       :class="{
                         'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 shadow-md': selectedTimeSlot === timeSlot.time,
                         'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700': timeSlot.scheduleCount > 0
                       }">
                    <div class="flex items-center space-x-3">
                      <div class="w-4 h-4 rounded-full"
                           :class="{
                             'bg-blue-500': selectedTimeSlot === timeSlot.time,
                             'bg-red-500': timeSlot.scheduleCount > 0 && selectedTimeSlot !== timeSlot.time,
                             'bg-gray-300 dark:bg-gray-600': timeSlot.scheduleCount === 0 && selectedTimeSlot !== timeSlot.time
                           }"></div>
                      <span class="font-semibold text-gray-900 dark:text-white text-base">{{ timeSlot.time }}</span>
                    </div>
                    <div v-if="timeSlot.scheduleCount > 0" 
                         class="flex items-center space-x-2 text-sm text-red-600 dark:text-red-400">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span class="font-medium">{{ t('kanban.schedule.scheduledCount', timeSlot.scheduleCount) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Motivos de Agendamento - Coluna Direita -->
            <div v-if="selectedTimeSlot" class="w-1/2">
              <div class="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 h-full">
                <h5 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
                  <svg class="w-4 h-4 mr-1 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  {{ t('kanban.schedule.reasons') }}
                </h5>
                <div class="grid grid-cols-1 gap-2 max-h-[560px] overflow-y-auto pr-1">
                  <button v-for="motivo in motivosAgendamento" 
                          :key="motivo.id"
                          @click="selectedMotivo = motivo.motivo"
                          class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md w-full"
                          :class="{
                            'shadow-md border-2': selectedMotivo === motivo.motivo,
                            'hover:bg-white dark:hover:bg-gray-600': selectedMotivo !== motivo.motivo
                          }"
                          :style="{ 
                            backgroundColor: selectedMotivo === motivo.motivo ? motivo.cor + '20' : motivo.cor + '10',
                            borderColor: selectedMotivo === motivo.motivo ? motivo.cor : 'transparent'
                          }">
                    <div class="flex items-center space-x-2 flex-1 min-w-0">
                      <div class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                           :style="{ backgroundColor: motivo.cor }">
                        {{ getMotivoIcon(motivo.icone) }}
                      </div>
                      <span class="font-medium text-gray-900 dark:text-white text-sm flex-1">{{ motivo.motivo }}</span>
                    </div>
                    <div v-if="selectedMotivo === motivo.motivo" class="text-white flex-shrink-0 ml-1">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Placeholder quando nenhum horário selecionado -->
            <div v-else class="w-1/2 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <div class="text-center bg-gray-50 dark:bg-gray-700/30 rounded-lg p-8 w-full">
                <svg class="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h6 class="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">{{ t('kanban.schedule.selectTime') }}</h6>
                <p class="text-sm text-gray-500 dark:text-gray-500">{{ t('kanban.schedule.selectTimeHint') }}</p>
              </div>
            </div>
          </div>

          <div v-else class="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div class="text-center">
              <svg class="w-12 h-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <p>{{ t('kanban.schedule.selectDateForTimes') }}</p>
            </div>
          </div>
        </div>
      </div>


      <!-- Footer -->
      <div class="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div class="flex items-center space-x-4">
          <div v-if="selectedDate && selectedTimeSlot && selectedMotivo" class="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span class="font-medium">{{ t('kanban.schedule.readyToSchedule') }}</span>
          </div>
          <div v-else class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>{{ t('kanban.schedule.selectDateTimeReason') }}</span>
          </div>
        </div>
        
        <div class="flex items-center space-x-3">
          <button @click="closeModal" 
                  class="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium">
            {{ t('common.cancel') }}
          </button>
          <button @click="confirmSchedule" 
                  :disabled="!selectedDate || !selectedTimeSlot || !selectedMotivo"
                  class="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:shadow-none">
            <span class="flex items-center space-x-2">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <span>{{ t('kanban.schedule.scheduleButton') }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import api from '../services/api'
import { getTagIcon } from '@/utils/iconMapper'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  task: {
    type: Object,
    default: null
  },
  lead: {
    type: Object,
    default: null
  },
  cliente: {
    type: Object,
    default: null
  },
  motivosAgendamento: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'scheduled'])

const { t, locale } = useI18n()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const weekdayLabels = computed(() => [
  t('kanban.schedule.weekdays.sun'),
  t('kanban.schedule.weekdays.mon'),
  t('kanban.schedule.weekdays.tue'),
  t('kanban.schedule.weekdays.wed'),
  t('kanban.schedule.weekdays.thu'),
  t('kanban.schedule.weekdays.fri'),
  t('kanban.schedule.weekdays.sat')
])

// Stores
const authStore = useAuthStore()

// Função para mapear ícones de motivos (usando o iconMapper centralizado)
const getMotivoIcon = (iconeName) => {
  if (!iconeName) return '📋'
  
  // Usar o iconMapper para converter o nome do ícone em emoji
  const icon = getTagIcon(iconeName)
  return icon || iconeName || '📋'
}

// Calendar state
const currentMonth = ref(new Date())
const selectedDate = ref(null)
const selectedTimeSlot = ref(null)
const selectedMotivo = ref('')
const monthScheduleData = ref({}) // Store schedule counts for each day

// Generate time slots from 7:00 to 22:00 (30min intervals)
const timeSlots = ref([])

// Initialize time slots
const initializeTimeSlots = () => {
  console.log('🕐 Inicializando time slots')
  const slots = []
  for (let hour = 7; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      slots.push({
        time: timeString,
        scheduleCount: 0
      })
    }
  }
  timeSlots.value = slots
  console.log('✅ Time slots inicializados:', timeSlots.value.length)
}

// Calendar days computation
const calendarDays = computed(() => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())
  
  const days = []
  const today = new Date()
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    
    const isCurrentMonth = date.getMonth() === month
    const isPast = date < today && !isSameDay(date, today)
    
    // Get schedule count for this day
    const dateKey = `${year}-${month}-${date.getDate()}`
    const scheduleCount = monthScheduleData.value[dateKey] || 0
    
    days.push({
      date: new Date(date),
      day: date.getDate(),
      isCurrentMonth,
      isPast,
      hasSchedules: scheduleCount > 0,
      scheduleCount: scheduleCount
    })
  }
  
  return days
})

// Helper functions
const isSameDay = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate()
}

const formatSelectedDate = (date) => {
  return date.toLocaleDateString(dateLocale.value, { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  })
}

// Calendar navigation
const previousMonth = async () => {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
  monthScheduleData.value = {} // Clear previous month data
  await loadMonthScheduleData()
}

const nextMonth = async () => {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
  monthScheduleData.value = {} // Clear previous month data
  await loadMonthScheduleData()
}

// Date and time selection
const selectDate = async (date) => {
  try {
    console.log('📅 Selecionando data:', date)
    selectedDate.value = new Date(date)
    selectedTimeSlot.value = null
    console.log('📅 Data selecionada:', selectedDate.value)
    console.log('📅 selectedDate.value é:', selectedDate.value)
    console.log('📅 timeSlots.value.length:', timeSlots.value.length)
    await loadScheduleData(date)
    console.log('📅 Dados carregados para a data')
  } catch (error) {
    console.error('❌ ERRO na função selectDate:', error)
    console.error('❌ Stack trace:', error.stack)
  }
}

const selectTimeSlot = (timeSlot) => {
  selectedTimeSlot.value = timeSlot.time
}

const isClienteMode = computed(() => props.cliente && props.cliente.id)

const scheduleEndpointForDate = (dateStr) => {
  if (props.lead) return `/agendamentos/lead/data/${dateStr}`
  if (isClienteMode.value) {
    const wsId = props.cliente.workspaceId
    return `/agendamentos/cliente/data/${dateStr}${wsId ? `?workspaceId=${wsId}` : ''}`
  }
  return `/agendamentos/data/${dateStr}`
}

// Load schedule data for the entire month
const loadMonthScheduleData = async () => {
  try {
    const token = localStorage.getItem('token')
    const year = currentMonth.value.getFullYear()
    const month = currentMonth.value.getMonth()
    
    // Get first and last day of the month
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    
    // Load data for each day of the month
    const promises = []
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      // Formatar data como YYYY-MM-DD
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      // Determinar endpoint baseado no tipo (Task ou Lead)
      const endpoint = scheduleEndpointForDate(dateStr)
      
      console.log(`🔍 Carregando agendamentos para ${dateStr} via ${endpoint}`)
      
      promises.push(
        api.get(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(response => {
          console.log(`✅ Resposta para ${dateStr}:`, response.data)
          return response
        }).catch(error => {
          console.log(`❌ Erro para ${dateStr}:`, error)
          return { data: [] } // Return empty array on error
        })
      )
    }
    
    const responses = await Promise.all(promises)
    
    // Store schedule counts in monthScheduleData
    responses.forEach((response, index) => {
      const day = index + 1
      const scheduleCount = response.data?.length || 0
      const dateKey = `${year}-${month}-${day}`
      monthScheduleData.value[dateKey] = scheduleCount
    })
    
  } catch (error) {
    console.error('❌ Erro ao carregar dados do mês:', error)
  }
}

// Load schedule data for selected date
const loadScheduleData = async (date) => {
  if (!date) return
  
  console.log('🔄 Carregando dados para data:', date)
  
  try {
    const token = localStorage.getItem('token')
    // Formatar data como YYYY-MM-DD
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    
    console.log('📅 Data formatada:', dateStr)
    
    // Determinar endpoint baseado no tipo (Task ou Lead)
    const endpoint = scheduleEndpointForDate(dateStr)
    
    console.log('🌐 Endpoint:', endpoint)
    
    const response = await api.get(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    console.log('✅ Resposta recebida:', response.data)
    
    const schedules = response.data || []
    
    // Update time slots with schedule counts
    timeSlots.value.forEach(slot => {
      const matchingSchedules = schedules.filter(schedule => schedule.horario === slot.time)
      slot.scheduleCount = matchingSchedules.length
    })
    
    console.log('⏰ Time slots atualizados')
    
  } catch (error) {
    console.error('❌ Erro ao carregar dados de agendamento:', error)
    // Reset schedule counts on error
    timeSlots.value.forEach(slot => {
      slot.scheduleCount = 0
    })
  }
}

// Confirm schedule
const confirmSchedule = async () => {
  if (!selectedDate.value || !selectedTimeSlot.value || !selectedMotivo.value || (!props.task && !props.lead && !isClienteMode.value)) return
  
  try {
    const token = localStorage.getItem('token')
    // Formatar data como YYYY-MM-DD
    const year = selectedDate.value.getFullYear()
    const month = selectedDate.value.getMonth() + 1
    const day = selectedDate.value.getDate()
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    
    // Determinar se é Task ou Lead
    const isTask = props.task && props.task.id
    const isLead = props.lead && props.lead.id
    const isCliente = isClienteMode.value
    
    if (isTask) {
      // 🔧 CORREÇÃO: Remover agendamentos anteriores antes de criar o novo (reagendamento)
      console.log('🔍 Verificando agendamentos anteriores da task:', props.task.id)
      try {
        const existingSchedules = await api.get(`/agendamentos/task/${props.task.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (existingSchedules.data && existingSchedules.data.length > 0) {
          console.log('🗑️ Removendo', existingSchedules.data.length, 'agendamento(s) anterior(es)')
          
          // Deletar todos os agendamentos anteriores
          for (const agendamento of existingSchedules.data) {
            console.log('🗑️ Removendo agendamento ID:', agendamento.id)
            await api.delete(`/agendamentos/${agendamento.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
          }
        }
      } catch (error) {
        console.error('❌ Erro ao remover agendamentos anteriores:', error)
        // Continuar mesmo se houver erro ao remover
      }
      
      // Criar novo agendamento
      console.log('✅ Criando novo agendamento')
      await api.post('/agendamentos', {
        taskId: props.task.id,
        usuarioId: authStore.user?.id || 1,
        data: dateStr,
        horario: selectedTimeSlot.value,
        motivo: selectedMotivo.value,
        observacoes: null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      emit('scheduled', {
        taskId: props.task.id,
        data: dateStr,
        horario: selectedTimeSlot.value
      })
    } else if (isLead) {
      // 🔧 CORREÇÃO: Remover agendamentos anteriores antes de criar o novo (reagendamento)
      console.log('🔍 Verificando agendamentos anteriores do lead:', props.lead.id)
      try {
        const existingSchedules = await api.get(`/agendamentos/lead/${props.lead.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (existingSchedules.data && existingSchedules.data.length > 0) {
          console.log('🗑️ Removendo', existingSchedules.data.length, 'agendamento(s) anterior(es)')
          
          // Deletar todos os agendamentos anteriores
          for (const agendamento of existingSchedules.data) {
            console.log('🗑️ Removendo agendamento do lead ID:', agendamento.id)
            await api.delete(`/agendamentos/lead/agendamento/${agendamento.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
          }
        }
      } catch (error) {
        console.error('❌ Erro ao remover agendamentos anteriores:', error)
        // Continuar mesmo se houver erro ao remover
      }
      
      // Criar novo agendamento
      console.log('✅ Criando novo agendamento para lead')
      await api.post('/agendamentos/lead', {
        leadId: props.lead.id,
        dataAgendamento: dateStr,
        horario: selectedTimeSlot.value,
        motivo: selectedMotivo.value,
        observacoes: null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      emit('scheduled', {
        leadId: props.lead.id,
        data: dateStr,
        horario: selectedTimeSlot.value
      })
    } else if (isCliente) {
      await api.post('/agendamentos/cliente', {
        clienteId: props.cliente.id,
        dataAgendamento: dateStr,
        horario: selectedTimeSlot.value,
        motivo: selectedMotivo.value,
        observacoes: null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      emit('scheduled', {
        clienteId: props.cliente.id,
        data: dateStr,
        horario: selectedTimeSlot.value
      })
    }
    
    closeModal()
    
  } catch (error) {
    console.error('❌ Erro ao agendar:', error)
    alert(t('kanban.schedule.scheduleError'))
  }
}

// Modal control
const closeModal = () => {
  console.log('🚪 closeModal chamado!')
  console.trace('🔍 Stack trace do closeModal:')
  selectedDate.value = null
  selectedTimeSlot.value = null
  selectedMotivo.value = ''
  emit('close')
}

// Initialize component
onMounted(() => {
  initializeTimeSlots()
})

// Watch for modal open to reset state
watch(() => props.isOpen, async (isOpen) => {
  console.log('👁️ Watch isOpen:', isOpen)
  if (isOpen) {
    console.log('🔄 Resetando estado do modal')
    console.log('📝 Motivos recebidos:', props.motivosAgendamento)
    console.log('📝 Quantidade de motivos:', props.motivosAgendamento?.length || 0)
    console.log('📝 Detalhes dos motivos:', props.motivosAgendamento)
    selectedDate.value = null
    selectedTimeSlot.value = null
    selectedMotivo.value = ''
    currentMonth.value = new Date()
    monthScheduleData.value = {} // Clear previous data
    await loadMonthScheduleData()
    console.log('✅ Modal resetado')
  }
})
</script>

<style>
/* Tema claro: só os números do calendário neste modal */
body.tema_clara .schedule-calendar-dates-grid .schedule-calendar-day-btn,
html.light .schedule-calendar-dates-grid .schedule-calendar-day-btn {
  color: #161616 !important;
  -webkit-text-fill-color: #161616 !important;
}

body.tema_clara .schedule-calendar-dates-grid .schedule-calendar-day-btn.bg-blue-500,
html.light .schedule-calendar-dates-grid .schedule-calendar-day-btn.bg-blue-500 {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

body.tema_clara .schedule-calendar-dates-grid .schedule-calendar-day-btn.bg-red-50,
html.light .schedule-calendar-dates-grid .schedule-calendar-day-btn.bg-red-50 {
  color: #dc2626 !important;
  -webkit-text-fill-color: #dc2626 !important;
}
</style>
